import * as yup from "yup";

import { hasNoData } from "@/react/schema/schemaFields";

import {
  VALIDATION_MESSAGES as ERRORS,
  ASSIGNMENT_DUPLICATE,
  AGENT_NAME_NOT_MATCHING,
  AGENT_ID_NOT_MATCHING,
  QUEUE_NAME_NOT_MATCHING,
  QUEUE_ID_NOT_MATCHING,
  MEDIA_TYPE_NOT_MATCHING,
} from "./constants";

export const agentIdAndNameAreValid = (value, ctx) => {
  if (hasNoData(ctx)) {
    return true;
  }

  let errors = [];
  const { queryFunctions } = ctx.options.context;
  const { getAgentById, getAgentByUsername } = queryFunctions;
  const { agentId, agentName } = value;

  if (agentId && !agentName) {
    errors.push(
      ctx.createError({
        path: ctx.path + "." + "agentName",
        message: ERRORS.AGENT_NAME_MISSING,
      })
    );
  } else if (!agentId && agentName) {
    errors.push(
      ctx.createError({
        path: ctx.path + "." + "agentId",
        message: ERRORS.AGENT_ID_MISSING,
      })
    );
  } else if (agentId && agentName) {
    if (
      agentId !== getAgentByUsername(agentName)?.sso.sso_id ||
      getAgentById(agentId)?.sso.sso_username !== agentName
    ) {
      const params = { agentName, agentId };
      errors.push(
        ctx.createError({
          path: ctx.path + "." + "agentId",
          message: ERRORS.format(AGENT_ID_NOT_MATCHING, params),
        })
      );
      errors.push(
        ctx.createError({
          path: ctx.path + "." + "agentName",
          message: ERRORS.format(AGENT_NAME_NOT_MATCHING, params),
        })
      );
    }
  }

  return errors.length === 0 ? true : new yup.ValidationError(errors);
};

export const queueIdAndNameAreValid = (value, ctx) => {
  if (hasNoData(ctx)) {
    return true;
  }

  let errors = [];
  const { queryFunctions } = ctx.options.context;
  const { getQueueById, getQueueByName } = queryFunctions;
  const { queueId, queueName } = value;

  if (queueId && !queueName) {
    errors.push(
      ctx.createError({
        path: ctx.path + "." + "queueName",
        message: ERRORS.QUEUE_NAME_MISSING,
      })
    );
  } else if (!queueId && queueName) {
    errors.push(
      ctx.createError({
        path: ctx.path + "." + "queueId",
        message: ERRORS.QUEUE_ID_MISSING,
      })
    );
  } else if (queueId && queueName) {
    if (
      queueId !== getQueueByName(queueName)?.id ||
      getQueueById(queueId)?.name !== queueName
    ) {
      const params = { queueName, queueId };
      errors.push(
        ctx.createError({
          path: ctx.path + "." + "queueId",
          message: ERRORS.format(QUEUE_ID_NOT_MATCHING, params),
        })
      );
      errors.push(
        ctx.createError({
          path: ctx.path + "." + "queueName",
          message: ERRORS.format(QUEUE_NAME_NOT_MATCHING, params),
        })
      );
    }
  }

  return errors.length === 0 ? true : new yup.ValidationError(errors);
};

export const assignmentIsUnique = (value, ctx) => {
  if (hasNoData(ctx)) {
    return true;
  }

  let errors = [];
  const { assignmentsMap } = ctx.options.context.metaData;
  const { agentName, queueName } = value;
  const checkAssignmentId = generateAssignmentKey(value);
  if (assignmentsMap.get(checkAssignmentId) > 1) {
    for (const field of ["agentId", "agentName", "queueId", "queueName"]) {
      errors.push(
        ctx.createError({
          path: ctx.path + "." + field,
          message: ERRORS.format(ASSIGNMENT_DUPLICATE, {
            agentName,
            queueName,
          }),
        })
      );
    }
  }

  return errors.length === 0 ? true : new yup.ValidationError(errors);
};

export const mediaTypeIsValid = (value, ctx) => {
  if (hasNoData(ctx)) {
    return true;
  }

  let errors = [];
  const { queryFunctions } = ctx.options.context;
  const { getQueueById, getQueueByName } = queryFunctions;
  const { queueId, queueName, mediaType } = value;

  const queue =
    (queueId && getQueueById(queueId)) ||
    (queueName && getQueueByName(queueName));

  if (queue) {
    if (!mediaType) {
      errors.push(
        ctx.createError({
          path: ctx.path + "." + "mediaType",
          message: ERRORS.MEDIA_TYPE_MISSING,
        })
      );
    } else if (mediaType !== queue.media_type) {
      errors.push(
        ctx.createError({
          path: ctx.path + "." + "mediaType",
          message: ERRORS.format(MEDIA_TYPE_NOT_MATCHING, {
            mediaType,
            queue: queueName ?? queueId,
          }),
        })
      );
    }
  }

  return errors.length === 0 ? true : new yup.ValidationError(errors);
};

export const generateAssignmentKey = (row) => {
  const { agentId, agentName, queueId, queueName } = row;
  return `${agentId}-${agentName}-${queueId}-${queueName}`;
};

// The pre-validation creates a map of assignments to their counts
// This way we can easily just check the map to see if an assignment is a duplicate
export const generateAssignmentCountMap = (rows) => {
  const seenMap = new Map();
  rows.map((row) => {
    const assignmentId = generateAssignmentKey(row);
    if (!seenMap.has(assignmentId)) {
      seenMap.set(assignmentId, 0);
    }
    seenMap.set(assignmentId, seenMap.get(assignmentId) + 1);
  });
  return { assignmentsMap: seenMap };
};
